import React, { useState } from "react";
import {
    Box,
    Button,
    Typography,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    Stack,
    Paper,
    Divider,
    Snackbar,
    Alert,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { fetchWithAuth } from "../../utils/api";

const AdminManageOptions = () => {
    const [options, setOptions] = useState(null);
    const [category, setCategory] = useState("");
    const [value, setValue] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleShowLists = async () => {
        try {
            const data = await fetchWithAuth("/token-search/options");
            setOptions(data);
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: "Failed to load options.", severity: "error" });
        }
    };

    const handleClear = () => {
        setOptions(null);
    };

    const handleSubmit = async () => {
        // --- Validation ---
        if (!category) {
            setSnackbar({ open: true, message: "Please select a category.", severity: "warning" });
            return;
        }

        if (!value.trim()) {
            setSnackbar({ open: true, message: "Please enter a value.", severity: "warning" });
            return;
        }

        // Split + clean values
        const valuesArray = value
            .split(",")
            .map(v => v.trim())
            .filter(v => v.length > 0);

        if (valuesArray.length === 0) {
            setSnackbar({ open: true, message: "Please enter at least one valid value.", severity: "warning" });
            return;
        }

        // Validate each value individually (NO parentheses allowed, matches your rule)
        const validPattern = /^[a-zA-Z0-9 :&'-]+$/;

        for (const val of valuesArray) {
            if (!validPattern.test(val)) {
                setSnackbar({
                    open: true,
                    message: `Invalid value: "${val}". Only letters, numbers, spaces, :, &, ', and - allowed.`,
                    severity: "warning",
                });
                return;
            }
        }

        // Normalize before sending (clean spacing)
        const cleanedValue = valuesArray.join(", ");

        try {
            await fetchWithAuth("/token-search/options", {
                method: "POST",
                body: { category: category, value: cleanedValue, is_active: true },
            });

            setSnackbar({
                open: true,
                message: "Option(s) added successfully!",
                severity: "success",
            });

            if (options) handleShowLists();
            setValue("");
            setCategory("");
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: "Failed to add option.", severity: "error" });
        }
    };

    const renderList = (title, list) => (
        <Paper
            elevation={3}
            sx={{
                p: 2,
                width: isMobile ? "100%" : "33%",
                maxHeight: 400,
                overflowY: "auto",
                textAlign: "center",
            }}
        >
            <Typography variant="h6" sx={{ mb: 1, color: theme.palette.accent.main }}>
                {title}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {list.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No options found.
                </Typography>
            ) : (
                list.map((opt, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 0.5, wordBreak: "break-word" }}>
                        {opt}
                    </Typography>
                ))
            )}
        </Paper>
    );

    return (
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: theme.palette.accent.main, mb: 3, textAlign: "center" }}
            >
                Admin: Manage Options
            </Typography>

            <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 3, alignItems: "center" }}>
                <Button variant="contained" color="primary" onClick={handleShowLists}>
                    Show Current Lists
                </Button>
                <Button variant="outlined" color="secondary" onClick={handleClear}>
                    Clear
                </Button>
            </Stack>

            {options && (
                <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={2}
                    sx={{ mb: 4, width: "100%", justifyContent: "center" }}
                >
                    {renderList("Prismatic Options", options.prismaticOptions)}
                    {renderList("Color Options", options.colorOptions)}
                    {renderList("Species List", options.speciesList)}
                </Stack>
            )}

            <Divider sx={{ my: 3, width: "100%" }} />

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: theme.palette.accent.main, mb: 2 }}>
                    Add New Option
                </Typography>

                <RadioGroup
                    row
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    sx={{ mb: 2 }}
                >
                    <FormControlLabel value="prismatic" control={<Radio />} label="Prismatic" />
                    <FormControlLabel value="color" control={<Radio />} label="Color" />
                </RadioGroup>

                <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ maxWidth: 400, width: "100%" }}>
                    <TextField
                        fullWidth
                        label="Option Value"
                        variant="outlined"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="Enter one or more values (comma separated)"
                    />
                    <Button variant="contained" color="primary" onClick={handleSubmit}>
                        Submit
                    </Button>
                </Stack>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminManageOptions;